import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, XCircle, Inbox } from 'lucide-react';
import { cn } from '../lib/utils.js';

/* ------------------------------------------------------------------ Alert */

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 text-sm [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:size-4 [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'border-border bg-card text-card-foreground [&>svg]:text-muted-foreground',
        info: 'border-info/30 bg-info/10 text-foreground [&>svg]:text-info',
        success: 'border-success/30 bg-success/10 text-foreground [&>svg]:text-success',
        warning: 'border-warning/40 bg-warning/10 text-foreground [&>svg]:text-warning',
        destructive:
          'border-destructive/40 bg-destructive/10 text-foreground [&>svg]:text-destructive',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const ALERT_ICON = {
  default: Info,
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  destructive: XCircle,
} as const;

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  /** false にするとアイコンを描かない。 */
  icon?: boolean;
}

export function Alert({ className, variant, icon = true, children, ...props }: AlertProps) {
  const Icon = ALERT_ICON[(variant ?? 'default') as keyof typeof ALERT_ICON];
  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      {icon ? <Icon aria-hidden /> : null}
      {children}
    </div>
  );
}

export function AlertTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h5 className={cn('mb-1 font-medium leading-none tracking-tight', className)} {...props} />;
}

export function AlertDescription({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('text-sm text-muted-foreground [&_p]:leading-relaxed', className)} {...props} />;
}

/* --------------------------------------------------------------- Skeleton */

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} {...props} />;
}

/**
 * テーブル読み込み中のプレースホルダ。
 *
 * **行の高さを実データの表とピクセル単位で揃える**（要件 §12.1
 * 「ローディングは行高を実データと揃えたスケルトンでレイアウトシフトを防ぐ」）。
 * 以前は `h-5` + `space-y-2`（28px / 行）で、ヘッダ行も無かったため、
 * `<Table>`（ヘッダ `h-10` + セル `py-2.5` の text-sm ＝ 40px / 行）に
 * 差し替わった瞬間に **6 行で 90px 以上ページが伸びていた**。
 * 一覧はページングで何度もこの状態を通るので、そのたびに視線が飛ぶ。
 *
 * 高さは `table.tsx` の `TableHead`（`h-10`）と `TableCell`（`px-3 py-2.5` +
 * `text-sm` の行高 20px）から来ている。**あちらを変えたらここも変えること。**
 */
const SKELETON_ROW_HEIGHT = 'h-[41px]'; // 20px（本文）+ 10px×2（py-2.5）+ 1px（border-b）

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div
      className="overflow-hidden rounded-lg border border-border bg-panel-elevated"
      aria-hidden
      data-testid="table-skeleton"
    >
      <div className="flex h-10 items-center gap-3 border-b border-border bg-panel px-3">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} className={cn('h-3 flex-1', c === 0 && 'max-w-[28%]')} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className={cn('flex items-center gap-3 border-b border-border px-3 last:border-0', SKELETON_ROW_HEIGHT)}
        >
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={cn('h-4 flex-1', c === 0 && 'max-w-[28%]')} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- EmptyState */

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-panel-elevated/50 px-6 py-14 text-center',
        className,
      )}
    >
      <div className="rounded-full bg-muted p-3">
        <Icon className="size-6 text-muted-foreground" />
      </div>
      <div className="space-y-1">
        <p className="font-medium text-foreground">{title}</p>
        {description ? (
          <p className="mx-auto max-w-md text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export { alertVariants };
